import React, { useCallback, useEffect, useState } from 'react';
import { Currency, Widget } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import BudgetTooltip from '../components/BudgetTooltip';
import ForecastTooltip from '../components/ForecastTooltip';
import PlannedBudgetTooltip from '../components/PlannedBudgetTooltip';
import {
  Box,
  Boxes,
  ChartContainer,
  Label,
  LowerBoxes,
  Metric,
  SmallBox,
  UpperBox,
  Value,
} from '../components/StyledComponents';
import ViewNav from '../components/ViewNav';
import ServicesCostByMonthChart from './ServicesCostByMonthChart';
import ServicesCostProgressChart from './ServicesCostProgressChart';

export default function ServicesCostWidget({ project, view, actualsThrough, approvalStatuses, onViewChange }) {
  const { workspace } = useWorkspace();

  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .dashboard()
        .progress.servicesCost({
          actualsThrough,
          approvalStatusId: approvalStatuses?.map(({ id }) => id),
        });
      setQuery({ data, isReady: true });
    } catch (error) {
      setQuery({ data: null, isReady: true });
    }
  }, [workspace.id, project.id, api, actualsThrough, approvalStatuses]);

  useEffect(() => {
    fetchData();
  }, [fetchData, project]);

  const boxes = {
    budget: !!data?.budget,
    forecast: data?.forecast?.dates.length > 0,
    get any() {
      return this.budget || this.forecast;
    },
  };

  const currency = project.currency;

  return (
    <Widget loading={!isReady}>
      <Widget.Preview style={{ minHeight: '32.4rem' }} />
      <Widget.Content>
        <ViewNav view={view} onViewChange={onViewChange} />

        {data && (
          <>
            <ChartContainer>
              {data &&
                {
                  progress: () => <ServicesCostProgressChart project={project} data={data} />,
                  month: () => <ServicesCostByMonthChart project={project} data={data} />,
                }[view]()}
            </ChartContainer>

            <Boxes>
              {project.isBillable && (
                <Box>
                  <UpperBox>
                    <Value>
                      <Currency
                        value={data.actual.billable}
                        currency={currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </Value>
                    <Label>Actual Billable Services Cost</Label>
                  </UpperBox>

                  {boxes.forecast && (
                    <LowerBoxes>
                      <SmallBox>
                        <Metric>
                          <ForecastTooltip
                            actual={data.actual.billable}
                            forecast={data.forecast.billable}
                            currency={currency}
                            metric="cost">
                            <Value>
                              <Currency
                                value={data.forecast.billable.forecasted}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Forecast (EAC)</Label>
                          </ForecastTooltip>
                        </Metric>
                        <Metric>
                          <Value>
                            <Currency
                              value={data.forecast.billable.left}
                              currency={currency}
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                            />
                          </Value>
                          <Label>Plan Left (ETC)</Label>
                        </Metric>
                      </SmallBox>
                    </LowerBoxes>
                  )}
                </Box>
              )}

              <Box>
                <UpperBox>
                  <Value>
                    <Currency
                      value={data.actual.nonBillable}
                      currency={currency}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </Value>
                  <Label>Actual Non-Billable Services Cost</Label>
                </UpperBox>
                {boxes.forecast && (
                  <LowerBoxes>
                    <SmallBox>
                      <Metric>
                        <ForecastTooltip
                          actual={data.actual.nonBillable}
                          forecast={data.forecast.nonBillable}
                          currency={currency}
                          metric="cost">
                          <Value>
                            <Currency
                              value={data.forecast.nonBillable.forecasted}
                              currency={currency}
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                            />
                          </Value>
                          <Label>Forecast (EAC)</Label>
                        </ForecastTooltip>
                      </Metric>
                      <Metric>
                        <Value>
                          <Currency
                            value={data.forecast.nonBillable.left}
                            currency={currency}
                            minimumFractionDigits={0}
                            maximumFractionDigits={0}
                          />
                        </Value>
                        <Label>Plan Left (ETC)</Label>
                      </Metric>
                    </SmallBox>
                  </LowerBoxes>
                )}
              </Box>

              <Box>
                <UpperBox>
                  <Value>
                    <Currency
                      value={data.actual.total}
                      currency={currency}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </Value>
                  <Label>Actual Total Services Cost</Label>
                </UpperBox>

                {boxes.any && (
                  <LowerBoxes>
                    {boxes.budget && (
                      <SmallBox>
                        <Metric>
                          <Value>
                            <Currency
                              value={data.budget.estimated ?? 0}
                              currency={currency}
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                            />
                          </Value>
                          <Label>Budget</Label>
                        </Metric>
                        <Metric status={data.budget.status}>
                          <BudgetTooltip value={data.budget.percent}>
                            <Value>
                              <Currency
                                value={data.budget.remaining}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Budget Left</Label>
                          </BudgetTooltip>
                        </Metric>
                      </SmallBox>
                    )}
                    {boxes.forecast && (
                      <SmallBox>
                        <Metric>
                          <ForecastTooltip
                            actual={data.actual.total}
                            forecast={data.forecast.total}
                            currency={currency}
                            metric="cost">
                            <Value>
                              <Currency
                                value={data.forecast.total.forecasted}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Forecast (EAC)</Label>
                          </ForecastTooltip>
                        </Metric>
                        <Metric status={data.forecast.total.status} data-testid="forecasted-left">
                          <PlannedBudgetTooltip value={data.forecast.total} currency={currency} metric="services cost">
                            <Value>
                              <Currency
                                value={data.forecast.total.left}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Plan Left (ETC)</Label>
                          </PlannedBudgetTooltip>
                        </Metric>
                      </SmallBox>
                    )}
                  </LowerBoxes>
                )}
              </Box>
            </Boxes>
          </>
        )}
      </Widget.Content>
    </Widget>
  );
}
